import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  FormArray,
} from '@angular/forms';
import { ModalController, LoadingController } from '@ionic/angular';
import { Insured } from 'src/app/models/models';
import { PopulationService } from 'src/app/services/population.service';
import { ModalService } from 'src/app/services/modal.service';
import { DataService } from 'src/app/services/data.service';
import { PickedFile } from '@capawesome/capacitor-file-picker';
import { SelectItemInterface } from 'src/app/components/select/select.component';
import { environment } from 'src/environments/environment';

interface RadiationDocument {
  id: string;
  label: string;
  required: boolean;
  file?: PickedFile;
}

interface RadiationReason extends SelectItemInterface {
  id: string;
  documents: RadiationDocument[];
}

@Component({
  selector: 'app-radiation-modal',
  templateUrl: './radiation-modal.component.html',
  styleUrls: ['./radiation-modal.component.scss'],
})
export class RadiationModalComponent implements OnInit {
  @Input() member: Insured;
  env = environment;
  radiationForm: FormGroup;
  enterpriseId: string;
  beneficiaries: Insured[] = [];
  selectedBeneficiaryIds = new Set<string>();
  radiationReasons: RadiationReason[] = [];
  selectedReasonDocuments: RadiationDocument[] = [];
  uploadedDocuments: Map<string, PickedFile> = new Map();

  get documentsArray(): FormArray {
    return this.radiationForm.get('documents') as FormArray;
  }

  constructor(
    private formBuilder: FormBuilder,
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private populationService: PopulationService,
    private modalService: ModalService,
    private dataService: DataService
  ) {}

  ngOnInit() {
    this.enterpriseId = this.dataService.selectedEnterprise.value.id;
    this.radiationForm = this.formBuilder.group(
      {
        effectiveDate: ['', [Validators.required]],
        reason: ['', [Validators.required]],
        includeAll: [false],
        selectedBeneficiaryIds: [[]],
        documents: this.formBuilder.array([]),
      },
      { validators: [this.selectionRequiredValidator()] }
    );

    // Subscribe to includeAll changes
    this.radiationForm.get('includeAll').valueChanges.subscribe((includeAll) => {
      if (includeAll) {
        // Check all beneficiaries
        this.selectedBeneficiaryIds = new Set(
          this.beneficiaries.map((b) => b.id)
        );
      } else {
        // Uncheck all beneficiaries
        this.selectedBeneficiaryIds.clear();
      }
      this.radiationForm
        .get('selectedBeneficiaryIds')
        .setValue(Array.from(this.selectedBeneficiaryIds), { emitEvent: false });
      this.radiationForm.updateValueAndValidity();
    });

    // Load radiation reasons
    this.loadRadiationReasons();

    // Ensure we have up-to-date beneficiaries list
    this.populationService.getMemberDetails(this.member.id).then((details) => {
      this.beneficiaries = details?.beneficiaries || [];
      this.radiationForm.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });
    });
  }

  async loadRadiationReasons() {
    try {
      this.populationService.getRadiationReasons().then((reasons) => {
        this.radiationReasons = reasons.map((reason) => ({
          ...reason,
          value: reason.id,
        }));
      });
    } catch (error) {
      console.error('Error loading radiation reasons:', error);
    }
  }

  onReasonChange(event: Event) {
    const reasonId = (event.target as HTMLSelectElement).value;
    const selectedReason = this.radiationReasons.find((r) => r.id === reasonId);

    if (selectedReason) {
      this.selectedReasonDocuments = selectedReason.documents;
      this.uploadedDocuments.clear();

      // Reset documents FormArray
      const documentsArray = this.radiationForm.get('documents') as FormArray;
      documentsArray.clear();

      // Add form controls for each document
      selectedReason.documents.forEach((doc) => {
        const validators = doc.required ? [Validators.required] : [];
        documentsArray.push(this.formBuilder.control(null, validators));
      });
    } else {
      this.selectedReasonDocuments = [];
      this.uploadedDocuments.clear();
      const documentsArray = this.radiationForm.get('documents') as FormArray;
      documentsArray.clear();
    }
  }

  onDocumentUploaded(files: PickedFile[], documentId: string, index: number) {
    if (files && files.length > 0) {
      this.uploadedDocuments.set(documentId, files[0]);
      const documentsArray = this.radiationForm.get('documents') as FormArray;
      documentsArray.at(index).setValue(files[0]);
    }
  }

  removeDocument(documentId: string, index: number) {
    this.uploadedDocuments.delete(documentId);
    const documentsArray = this.radiationForm.get('documents') as FormArray;
    documentsArray.at(index).setValue(null);
  }

  onIncludeAllChange(event: Event) {
    const checked = !!(event?.target as HTMLInputElement)?.checked;
    if (checked) {
      this.selectedBeneficiaryIds = new Set(
        this.beneficiaries.map((b) => b.id)
      );
    } else {
      this.selectedBeneficiaryIds.clear();
    }
    this.radiationForm
      .get('selectedBeneficiaryIds')
      .setValue(Array.from(this.selectedBeneficiaryIds));
    this.radiationForm.updateValueAndValidity();
  }

  onBeneficiaryChange(event: Event, id: string) {
    // Guard: don't allow changes when includeAll is true
    if (this.radiationForm.get('includeAll')?.value) {
      event.preventDefault();
      return;
    }

    // Stop event propagation to prevent it from affecting other checkboxes
    event.stopPropagation();

    const checked = !!(event?.target as HTMLInputElement)?.checked;
    if (checked) {
      this.selectedBeneficiaryIds.add(id);
    } else {
      this.selectedBeneficiaryIds.delete(id);
    }
    this.radiationForm
      .get('selectedBeneficiaryIds')
      .setValue(Array.from(this.selectedBeneficiaryIds), { emitEvent: false });
    this.radiationForm.updateValueAndValidity();
  }

  private selectionRequiredValidator() {
    return (group: AbstractControl) => {
      const includeAll = group.get('includeAll')?.value;
      const selected: string[] =
        group.get('selectedBeneficiaryIds')?.value || [];
      const hasBeneficiaries = (this.beneficiaries?.length || 0) > 0;
      if (
        hasBeneficiaries &&
        !includeAll &&
        (!selected || selected.length === 0)
      ) {
        return { selectionRequired: true };
      }
      return null;
    };
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async submitRadiation() {
    if (this.radiationForm.invalid) {
      this.radiationForm.markAllAsTouched();
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Traitement de la radiation en cours...',
    });
    await loading.present();

    try {
      const includeAll: boolean = this.radiationForm.value.includeAll;
      // Send empty array when includeAll is true (salarié + all beneficiaries)
      // Send selected beneficiary IDs when specific beneficiaries are selected
      const beneficiaryIds = includeAll
        ? []
        : Array.from(this.selectedBeneficiaryIds);

      // Prepare documents data
      const documents = Array.from(this.uploadedDocuments.entries()).map(
        ([docId, file]) => ({
          id: docId,
          name: file.name,
          data: file.data,
          mimeType: file.mimeType,
        })
      );

      await this.populationService.radiateMember(
        this.enterpriseId,
        this.member.id,
        this.radiationForm.value.effectiveDate,
        beneficiaryIds,
        this.radiationForm.value.reason,
        documents
      );

      await loading.dismiss();

      this.modalService.createAlertModal({
        title: 'Demande de radiation envoyée',
        message: `La demande de radiation de ${this.member.firstname} ${this.member.lastname} a été envoyée avec succès à la mutuelle.`,
        actionRequested: false,
      });

      this.modalCtrl.dismiss({ success: true });
    } catch (error) {
      await loading.dismiss();

      this.modalService.createAlertModal({
        title: 'Erreur',
        message:
          'Une erreur est survenue lors de la radiation. Veuillez réessayer.',
        actionRequested: false,
      });
    }
  }
}
